import { Box, Link } from '@mui/material'
import { ReactNode } from 'react'

// Banner text is plain text with a few Markdown-style tokens:
//   # Heading (also ## and ###, at the start of a line)
//   **bold**  *italic*  ++underline++  [text](https://…)
// A backslash shows the next marker character as written: \\ \* \+ \[ \] \#
// It is never parsed as HTML, so anything else (tags, colours, images) shows as written.
// As in Markdown, a marker next to a space isn't one, so "5 * 3" stays as it is. A closing **
// can't be followed by another *, so bold ending in italic ("**a *b***") closes at the end.
const TOKEN =
  /\*\*(?=\S)(.+?)(?<=\S)\*\*(?!\*)|\+\+(?=\S)(.+?)(?<=\S)\+\+(?!\+)|\*(?=\S)(.+?)(?<=\S)\*|\[([^\]\n]+)\]\(([^\s()]+)\)/g
// No spaces or brackets, as those would end the link in the text.
const SAFE_HREF = /^(https?:\/\/|mailto:|\/(?!\/))[^\s()]*$/i

export const isSafeHref = (href: string) => SAFE_HREF.test(href)

const ESCAPABLE = '\\*+[]#'

export const escapeBannerText = (text: string) => text.replace(/[\\*+[\]]/g, '\\$&')

// While parsing, escaped characters are swapped for private-use ones that no pattern matches.
const hide = (text: string) =>
  text.replace(/\\([\\*+[\]#])/g, (_, char: string) =>
    String.fromCharCode(0xe000 + ESCAPABLE.indexOf(char))
  )
const unhide = (text: string) =>
  text.replace(/[\ue000-\ue005]/g, (char) => ESCAPABLE[char.charCodeAt(0) - 0xe000])

export type TextFormat = 'bold' | 'italic' | 'underline'
export type HeadingTag = 'h1' | 'h2' | 'h3'

export const HEADING_TAGS: HeadingTag[] = ['h1', 'h2', 'h3']

// Sized relative to the banner text, not the page headings.
export const HEADING_STYLES = {
  h1: { m: 0, fontSize: '1.375em', fontWeight: 700, lineHeight: 1.3 },
  h2: { m: 0, fontSize: '1.2em', fontWeight: 700, lineHeight: 1.3 },
  h3: { m: 0, fontSize: '1.05em', fontWeight: 600, lineHeight: 1.3 },
}

// `start` is unique among siblings, so it doubles as a React key.
export type BannerTextPart =
  | string
  | { kind: TextFormat; start: number; children: BannerTextPart[] }
  | { kind: 'link'; start: number; href: string; children: BannerTextPart[] }

// Takes text with escaped characters hidden.
function parseInline(text: string): BannerTextPart[] {
  const parts: BannerTextPart[] = []
  let lastIndex = 0

  for (const match of text.matchAll(TOKEN)) {
    const [raw, bold, underline, italic, label, href] = match
    const start = match.index
    if (start > lastIndex) {
      parts.push(unhide(text.slice(lastIndex, start)))
    }
    lastIndex = start + raw.length

    if (bold !== undefined) {
      parts.push({ kind: 'bold', start, children: parseInline(bold) })
    } else if (underline !== undefined) {
      parts.push({ kind: 'underline', start, children: parseInline(underline) })
    } else if (italic !== undefined) {
      parts.push({ kind: 'italic', start, children: parseInline(italic) })
    } else if (isSafeHref(unhide(href))) {
      parts.push({ kind: 'link', start, href: unhide(href), children: parseInline(label) })
    } else {
      parts.push(unhide(raw))
    }
  }

  if (lastIndex < text.length) {
    parts.push(unhide(text.slice(lastIndex)))
  }
  return parts
}

export const parseBannerLine = (
  line: string
): { tag: HeadingTag | null; parts: BannerTextPart[] } => {
  const text = hide(line)
  const heading = /^(#{1,3}) (.*)$/.exec(text)
  return heading
    ? { tag: `h${heading[1].length}` as HeadingTag, parts: parseInline(heading[2]) }
    : { tag: null, parts: parseInline(text) }
}

const plainText = (parts: BannerTextPart[]): string =>
  parts.map((part) => (typeof part === 'string' ? part : plainText(part.children))).join('')

// The text without its markup, on one line.
export const toPlainBannerText = (text: string) =>
  text
    .split('\n')
    .map((line) => plainText(parseBannerLine(line).parts).trim())
    .filter(Boolean)
    .join(' ')

const TAGS = { bold: 'strong', italic: 'em', underline: 'u' } as const

const renderParts = (parts: BannerTextPart[]): ReactNode[] =>
  parts.map((part) => {
    if (typeof part === 'string') {
      return part
    }
    const children = renderParts(part.children)
    if (part.kind === 'link') {
      // --banner-accent is set by BannerCard.
      return (
        <Link
          key={part.start}
          href={part.href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'var(--banner-accent)',
            fontWeight: 600,
            textDecorationColor: 'currentColor',
          }}
        >
          {children}
        </Link>
      )
    }
    return (
      <Box key={part.start} component={TAGS[part.kind]}>
        {children}
      </Box>
    )
  })

export const renderBannerText = (text: string): ReactNode[] => {
  let start = 0
  return text.split('\n').map((line) => {
    const key = start
    start += line.length + 1
    const { tag, parts } = parseBannerLine(line)
    return (
      <Box key={key} component={tag ?? 'div'} sx={tag ? HEADING_STYLES[tag] : undefined}>
        {parts.length ? renderParts(parts) : <br />}
      </Box>
    )
  })
}
